import { memo, useCallback } from 'react'

import type { TextFieldProps } from '@/components/ui/text-field'
import { TextField } from '@/components/ui/text-field'
import { classNames } from '@/utils/helpers'

import { useSalesEditor } from '../hooks/use-sales-editor'

type Props<Value extends number | string> = Omit<TextFieldProps, 'onChange'> & {
  value: Value
  format?: (value: string) => string
  onChange?: (value: string) => void
  showInputIf?: boolean
}

export const SalesEditableInput = memo(
  <Value extends number | string>({
    value,
    format,
    onChange,
    className,
    showInputIf = true,
    ...props
  }: Props<Value>) => {
    const { isEditing, activeTemplate } = useSalesEditor()

    const handleChange: React.ChangeEventHandler<HTMLInputElement> =
      useCallback(
        (event) => {
          onChange?.(event.target.value)
        },
        [onChange],
      )

    if (!isEditing || !activeTemplate || !showInputIf) {
      return format ? format(String(value)) : value
    }

    return (
      <TextField
        className={classNames('max-w-[200px]', className)}
        onChange={handleChange}
        value={value}
        {...props}
      />
    )
  },
)

SalesEditableInput.displayName = 'SalesEditableInput'
